import { useEffect, useRef } from "react";
import { X, Check, CaretRight, Gift } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import useUserStats from "@/lib/queries/user/useUserStats";
import { Link } from "react-router-dom";
import { useStytchSession } from "@stytch/react";
import { useIsMobile } from "@/hooks/use-mobile";
import useUser from "@/lib/queries/user/useUser";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";

interface ProgressStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  link?: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

interface ProgressStepItemProps {
  step: ProgressStep;
  t: (key: string, options?: any) => string;
}

// interface ProgressBarProps {
//   completed: number;
//   total: number;
// }

const ProgressStepItem = ({ step, t }: ProgressStepItemProps) => {
  const isDisabled = step.disabled && !step.completed;

  return (
    <div
      className={cn(
        "flex items-center gap-3 group",
        isDisabled && "cursor-not-allowed"
      )}
      role="listitem"
    >
      <div
        className={cn(
          "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2",
          step.completed
            ? "border-transparent"
            : isDisabled
              ? "bg-gray-50 text-gray-400 border-gray-300 border-dashed"
              : "bg-gray-100 text-gray-400 border-gray-200"
        )}
        style={
          step.completed
            ? {
                backgroundColor: "hsl(var(--light-green))",
                color: "hsl(var(--dark-green))",
              }
            : {}
        }
        aria-hidden="true"
      >
        {step.completed ? (
          <Check className="w-4 h-4" />
        ) : (
          <div className="w-2 h-2 bg-current rounded-full" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h4
          className={cn(
            "text-sm font-medium",
            step.completed
              ? "text-gray-600 line-through"
              : isDisabled
                ? "text-gray-500"
                : "text-gray-900"
          )}
        >
          {step.title}
        </h4>
        {/* <p
          className={cn(
            "text-xs text-wrap max-w-80",
            isDisabled ? "text-gray-500" : "text-gray-500"
          )}
        >
          {step.description}
          {isDisabled && (
            <span className="ml-1 font-medium text-orange-600">
              (Requires account)
            </span>
          )}
        </p> */}
      </div>

      {!step.completed && step.link && !isDisabled && (
        <Link
          to={step.link}
          aria-label={t("progressChecklist.stepAction.goTo", {
            title: step.title,
          })}
        >
          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-0 transition-opacity opacity-0 group-hover:opacity-100"
            aria-label={t("progressChecklist.stepAction.navigateTo", {
              title: step.title,
            })}
          >
            <CaretRight className="w-3 h-3" />
          </Button>
        </Link>
      )}

      {isDisabled && (
        <div className="flex-shrink-0">
          <div className="flex items-center justify-center w-6 h-6 border border-orange-200 rounded-full bg-orange-50">
            <div className="w-2 h-2 bg-orange-400 rounded-full" />
          </div>
        </div>
      )}
    </div>
  );
};

// const ProgressBar = ({ completed, total }: ProgressBarProps) => {
//   const percentage = Math.round((completed / total) * 100);

//   return (
//     <section
//       className="p-4 border-b border-gray-100"
//       aria-labelledby="progress-heading"
//     >
//       <div className="flex items-center justify-between mb-2">
//         <span id="progress-heading" className="text-sm text-gray-600">
//           Progress
//         </span>
//         <span className="text-sm font-medium text-gray-900" aria-live="polite">
//           {percentage}%
//         </span>
//       </div>
//       <div
//         className="w-full h-2 bg-gray-200 rounded-full"
//         role="progressbar"
//         aria-valuenow={percentage}
//         aria-valuemin={0}
//         aria-valuemax={100}
//         aria-label={`Progress: ${completed} of ${total} steps completed`}
//       >
//         <div
//           className="h-2 transition-all duration-300 rounded-full"
//           style={{
//             width: `${percentage}%`,
//             backgroundColor: "hsl(var(--primary))",
//           }}
//         />
//       </div>
//     </section>
//   );
// };

const ProgressChecklist = () => {
  const { t } = useTranslation("common");
  const { data: userStats, isLoading } = useUserStats();
  const { session } = useStytchSession();
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useLocalStorage<boolean>(
    "progress-checklist-dismissed",
    true
  );

  const { data: user, isLoading: isUserLoading } = useUser({
    enabled: session !== null,
  });

  const previousCompletionsRef = useRef<boolean[] | null>(null);

  const steps: ProgressStep[] = !userStats
    ? []
    : [
        {
          id: "commGenerator",
          title: t("progressChecklist.step.commGenerator.title"),
          description: t("progressChecklist.step.commGenerator.description"),
          completed: !!userStats.toolUses.communicationsgenerator
            ? userStats.toolUses.communicationsgenerator > 0
            : false,
          link: "/writing-wizard",
          icon: <Check className="w-4 h-4" />,
          disabled: false,
        },
        {
          id: "researcher",
          title: t("progressChecklist.step.researcher.title"),
          description: t("progressChecklist.step.researcher.description"),
          completed: !!userStats.toolUses.researcher
            ? userStats.toolUses.researcher > 0
            : false,
          link: "/researcher",
          icon: <Check className="w-4 h-4" />,
          disabled: false,
        },
        {
          id: "registered",
          title: t("progressChecklist.step.registered.title"),
          description: t("progressChecklist.step.registered.description"),
          completed: !!userStats.progress.registered,
          link: session ? undefined : "/authenticate/signup",
          icon: <Check className="w-4 h-4" />,
          disabled: false,
        },
        {
          id: "nameSet",
          title: t("progressChecklist.step.nameSet.title"),
          description: t("progressChecklist.step.nameSet.description"),
          completed: !!userStats.progress.nameSet,
          link: session ? "/settings" : undefined,
          icon: <Check className="w-4 h-4" />,
          disabled: !session,
        },
        {
          id: "checkpoint1",
          title: t("progressChecklist.step.checkpoint1.title"),
          description: t("progressChecklist.step.checkpoint1.description"),
          completed: userStats.progress.extraTrialReceived ? true : false,
          link: undefined,
          icon: <Gift className="w-4 h-4 text-primary" />,
          disabled: false,
        },
      ];

  const secondarySteps: ProgressStep[] = !userStats
    ? []
    : [
        {
          id: "lessonLab3",
          title: t("progressChecklist.step.lessonLab3.title"),
          description: t("progressChecklist.step.lessonLab3.description"),
          completed: !!userStats.toolUses.lessonlab
            ? userStats.toolUses.lessonlab >= 3
            : false,
          link: session ? "/lesson-lab" : undefined,
          icon: <Check className="w-4 h-4" />,
          disabled: !session,
        },
        {
          id: "resourceCustomiser3",
          title: t("progressChecklist.step.resourceCustomiser3.title"),
          description: t(
            "progressChecklist.step.resourceCustomiser3.description"
          ),
          completed: !!userStats.toolUses.resourcecustomiser
            ? userStats.toolUses.resourcecustomiser >= 3
            : false,
          link: session ? "/resource-customiser" : undefined,
          icon: <Check className="w-4 h-4" />,
          disabled: !session,
        },
        {
          id: "subscribe",
          title: t("progressChecklist.step.subscribe.title"),
          description: t("progressChecklist.step.subscribe.description"),
          completed: user?.subscribed ? true : false,
          link: session ? "/settings/subscription" : undefined,
          icon: <Check className="w-4 h-4" />,
          disabled: !session,
        },
        {
          id: "checkpoint2",
          title: t("progressChecklist.step.checkpoint2.title"),
          description: t("progressChecklist.step.checkpoint2.description"),
          completed: userStats.progress.creditReceived ? true : false,
          link: undefined,
          icon: <Gift className="w-4 h-4 text-primary" />,
          disabled: false,
        },
      ];

  const checkpoint1Completed = !!userStats?.progress?.extraTrialReceived;
  const currentSteps = checkpoint1Completed ? secondarySteps : steps;

  const completedSteps = currentSteps.filter((step) => step.completed).length;
  const totalSteps = currentSteps.length;

  useEffect(() => {
    if (!userStats) return;
    const currentCompletions = currentSteps.map((s) => s.completed);
    const prev = previousCompletionsRef.current;

    const hasNewCompletion = prev
      ? currentCompletions.some((c, i) => c && prev[i] === false)
      : false;

    if (hasNewCompletion && dismissed) {
      setDismissed(false);
    }

    previousCompletionsRef.current = currentCompletions;
  }, [
    userStats,
    currentSteps.map((s) => s.completed).join("|"),
    completedSteps,
    totalSteps,
  ]);

  if (
    isUserLoading ||
    isLoading ||
    !userStats ||
    (user?.subscribed &&
      userStats?.progress.creditReceived &&
      userStats?.progress.extraTrialReceived)
  ) {
    return null;
  }

  if (dismissed) {
    return (
      <aside
        className={cn(
          "fixed z-50",
          isMobile
            ? "top-20 right-4" // Underneath mobile nav
            : "top-4",
          !isMobile && (import.meta.env.DEV ? "right-16" : "right-4")
        )}
        aria-label="Progress checklist minimized"
      >
        <Button
          onClick={() => {
            setDismissed(false);
          }}
          variant="default"
          size="sm"
          className="rounded-full shadow-lg dark:shadow-white"
          aria-label={t("progressChecklist.minimized.open", {
            completedSteps,
            totalSteps,
          })}
        >
          <Gift className="w-4 h-4 mr-2" />
          <span aria-live="polite">
            {completedSteps}/{totalSteps}
          </span>
        </Button>
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        "fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl transition-all duration-300",
        isMobile
          ? "top-20 right-4 left-4 w-auto" // Full width on mobile, underneath mobile nav
          : "top-4 w-fit",
        !isMobile && (import.meta.env.DEV ? "right-16" : "right-4")
      )}
      aria-label="Progress checklist"
      role="complementary"
    >
      <header className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" aria-hidden="true" />
          <h3 className="font-semibold text-gray-900">
            {checkpoint1Completed
              ? t("progressChecklist.advancedGoals")
              : t("progressChecklist.earnExtraUses")}
          </h3>
          <Badge variant="secondary" className="text-xs" aria-live="polite">
            {completedSteps}/{totalSteps}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDismissed(true);
          }}
          className="w-6 h-6 p-0"
          aria-label={t("progressChecklist.close")}
        >
          <X className="w-4 h-4" />
        </Button>
      </header>

      {/* <ProgressBar completed={completedSteps} total={totalSteps} /> */}

      <main className="p-4 space-y-3 overflow-y-auto ">
        <ol className="space-y-3" role="list" aria-label="Progress steps">
          {currentSteps.map((step) => (
            <ProgressStepItem key={step.id} step={step} t={t} />
          ))}
        </ol>
      </main>
    </aside>
  );
};

export default ProgressChecklist;
